import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Paperclip, Send, X } from 'react-feather';
import ReactLoading from 'react-loading';
import classnames from 'classnames';
import { isEmpty, throttle } from 'lodash';

import MentionList from '../MentionList/MentionListContainer';
import MessageInputErrorBar from '../MessageInputErrorBar';
import UpdoxEmojiPicker from '../UpdoxEmojiPicker/UpdoxEmojiPicker';

import { ACCEPTED_FILE_TYPES } from '../../constants/FileTypes';
import { MAXIMUM_CHARACTER_COUNT_WARNING } from '../../constants/ErrorMessageConstants';
import { MESSAGE_CHARACTER_LIMIT, TYPING_BEGIN } from '../../constants/AppConstants';
import { logError } from '../../helpers/BrowserUtils';

const DEFAULT_MESSAGE_TYPE = 'text/plain';

export default class MessageInput extends Component {
  static propTypes = {
    userIds: PropTypes.array,
    senderId: PropTypes.number,
    chatId: PropTypes.number,
    addMessage: PropTypes.func,
    setTyping: PropTypes.func,
    autoFocus: PropTypes.bool,
    messageInputData: PropTypes.object,
    saveMessageInputData: PropTypes.func.isRequired,
    onSendMessage: PropTypes.func,
  };

  constructor(props) {
    super(props);
    const messageInputData = props.messageInputData;
    if (messageInputData) {
      this.state = messageInputData;
    } else {
      this.state = this.emptyState;
    }
    // The ref is available to provide focus after another component affects the input.
    this.messageTextInput = React.createRef();

    this.handleThrottleTyping = throttle(this.throttleTyping, TYPING_BEGIN);
  }

  emptyState = {
    input: '',
    inputCursorPos: 0,
    files: {},
    submitting: false,
  };

  componentDidUpdate(prevProps) {
    const prevChatId = prevProps.chatId;
    if (prevChatId !== this.props.chatId) {
      // First, save the current messageInput data into redux state
      this.saveMessageInputData(prevChatId);

      // Then, Load the new chats messageInput data or initialize to empty state
      const messageInputData = this.props.messageInputData;
      if (messageInputData) {
        this.setState(messageInputData);
      } else {
        this.setState(this.emptyState);
      }

      /*
       * Switching chat's doesn't trigger the auto focus because the messageTextInput element isn't being destroyed and recreated.
       * If the chatId has changed let's set the focus back to the messageTextInput
       */
      if (this.props.autoFocus) {
        this.messageTextInput.current.focus();
      }
    }
  }

  componentWillUnmount() {
    // We need to save the messageInputData if we go to a a different screen such as the directory
    this.saveMessageInputData(this.props.chatId);
  }

  saveMessageInputData = (chatId) => {
    const { input, inputCursorPos, files, submitting } = this.state;
    const prevMessageInputData = { input, inputCursorPos, files, submitting };
    this.props.saveMessageInputData(chatId, prevMessageInputData);
  };

  handleClickPositionChange = (event) => {
    this.setState({ inputCursorPos: event.target.selectionStart });
  };

  handleArrowKeyPositionChange = (event) => {
    const key = event.key || event.which || event.keyCode;
    const text = event.target.value;
    const pos = event.target.selectionStart;

    switch (key) {
      case 'ArrowLeft':
      case 37:
        if (pos > 0) this.setState({ inputCursorPos: (pos-1) });
        break;
      case 'ArrowRight':
      case 39:
        if (pos < text.length) this.setState({ inputCursorPos: (pos+1) });
        break;
      default:
        break;
    }
  };

  submitMessage = async (e) => {
    e.preventDefault();
    const { files, input, submitting } = this.state;
    const { chatId, userIds, addMessage, onSendMessage } = this.props;

    const messageHasContent = !isEmpty(input) || !isEmpty(files);

    // if a message is in the process of being submitted, reject the submission
    // OR if the user is creating a new Direct Message without selecting users, reject the submission
    if (submitting || (userIds && userIds.length < 1) || !messageHasContent) return false;

    try {
      // Convert FileList to Array
      const filesArr = !isEmpty(files) ? [...files] : [];

      await addMessage(chatId, input, DEFAULT_MESSAGE_TYPE, filesArr);

      onSendMessage();

      document.getElementById('file-upload').value = '';
      this.setState({ input: '', files: {}, submitting: false });
    } catch (e) {
      logError('Failed to upload file: ', e);
      this.setState({ submitting: false });
    }
  };

  addTextToInput = (text) => {
    const { input, inputCursorPos } = this.state;
    const nextInput = [input.substring(0, inputCursorPos), text, input.substring(inputCursorPos)].join('');
    this.textChangeCallback(nextInput);
  };

  /**
   * Callback used when an child component updates the text in the input box.
   * @param text the new text value for the input.
   */
  textChangeCallback = (text) => {
    const { input, inputCursorPos } = this.state;
    text = text.substring(0, MESSAGE_CHARACTER_LIMIT);
    const lengthDiff = text.length - input.length;
    const updatedCursorPos = inputCursorPos + lengthDiff;

    this.handleThrottleTyping();

    this.setState({
      input: text,
      inputCursorPos: updatedCursorPos,
    }, () => { // This function is called after the state has been updated

      this.messageTextInput.current.focus();
      this.messageTextInput.current.selectionStart = updatedCursorPos;
      this.messageTextInput.current.selectionEnd = updatedCursorPos;

    });
  };

  throttleTyping = () => {
    this.props.setTyping(this.props.chatId, true);
  };

  handleTextChange = (event) => {
    const { value, selectionStart } = event.target;
    const { chatId } = this.props;

    chatId && this.handleThrottleTyping();

    this.setState({
      input: value,
      inputCursorPos: selectionStart,
    });
  };

  handleFileChange = (event) => {
    this.setState({ files: event.target.files });
    this.messageTextInput.current.focus();
  };

  removeFile = () => {
    this.setState({ files: {} });
    document.getElementById('file-upload').value = '';
  };

  selectEmoji = (emoji) => {
    this.addTextToInput(emoji);
  };

  render() {
    const { input, inputCursorPos, files, submitting } = this.state;
    return (
      <div className="message-input">
        { input.length < MESSAGE_CHARACTER_LIMIT && <MentionList text={input} textCursorPos={inputCursorPos} textChangeCallback={this.textChangeCallback}/> }
        { input.length >= MESSAGE_CHARACTER_LIMIT && <MessageInputErrorBar text={MAXIMUM_CHARACTER_COUNT_WARNING}/> }
        {files[0] && files[0].name &&
          <div
            className="filename"
            data-qa="attachmentFilename"
          >
            <span>{files[0].name}</span>
            <button
              onClick={this.removeFile}
              type="button"
            >
              <X size={16} />
            </button>
          </div>
        }

        <form className={classnames({ 'attachments-showing': files[0] && files[0].name })}
          onSubmit={this.submitMessage}
          encType="multipart/form-data"
          method="post"
          data-qa="chatMessageInput"
        >
          <label
            htmlFor="file-upload"
            className="file-upload icon-link subtle"
          >
            <input
              id="file-upload"
              type="file"
              onChange={this.handleFileChange}
              accept={ACCEPTED_FILE_TYPES}
              title="File Upload"
            />
            <Paperclip size={20}/>
          </label>
          <UpdoxEmojiPicker onSelect={this.selectEmoji} />
          <label htmlFor="messageInput" className="WCAG-label">Message Input</label>
          <input
            id="messageInput"
            type="text"
            name="message"
            value={input}
            placeholder="Enter Message..."
            autoComplete="off"
            onChange={this.handleTextChange}
            onKeyDown={this.handleArrowKeyPositionChange}
            onClick={this.handleClickPositionChange}
            className={classnames({
              'attachments-showing': files[0] && files[0].name,
            })}
            autoFocus={this.props.autoFocus}
            ref={this.messageTextInput}
            maxLength={MESSAGE_CHARACTER_LIMIT}
          />
          {submitting ?
            <button
              className="icon-link subtle send-btn"
              data-qa="sendButtonLoading"
            >
              <ReactLoading
                type="spin"
                color="#333" height={16} width={16}
              />
            </button>
            :
            <button
              onClick={this.submitMessage}
              className="icon-link subtle send-btn"
              data-qa="sendButton"
              type="submit"
            >
              <Send size={20} />
            </button>
          }
        </form>
      </div>
    );
  }
}
